import {
  afterRenderEffect,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { NgControl } from '@angular/forms';
import { FormInputComponent } from '../form-input.component';
import { ThemeComponent } from '../../../../theming';

export interface IButtonOption<T> {
  label: string;
  value: T;
  iconSrc?: string;
}

@Component({
  selector: 'aw-button-group',
  templateUrl: './button-group.component.html',
  imports: [NgClass],
})
export class ButtonGroupComponent<T>
  extends FormInputComponent
  implements OnInit
{
  readonly options = input.required<IButtonOption<T>[]>();
  readonly buttonWidth = input(196);
  readonly buttonsPerRow = input(0);
  readonly compact = input(false);
  readonly ariaLabel = input<string>();
  readonly valueChange = output<string>();

  protected readonly theme = inject(ThemeComponent);
  private readonly buttonGroupRef =
    viewChild<ElementRef<HTMLElement>>('buttonGroup');
  private readonly containerWidth = signal(0);
  private readonly preferredWidth = computed(() =>
    Math.max(1, this.buttonWidth())
  );
  protected readonly columns = computed(() =>
    this.buttonsPerRow() > 0
      ? Math.max(1, Math.floor(this.buttonsPerRow()))
      : Math.max(1, Math.floor(this.containerWidth() / this.preferredWidth()))
  );
  protected readonly gridColumns = computed(() =>
    this.buttonsPerRow() > 0
      ? `repeat(${this.columns()}, minmax(0, 1fr))`
      : `repeat(auto-fill, minmax(min(100%, ${this.preferredWidth()}px), ${this.preferredWidth()}px))`
  );

  protected readonly items = computed(() => {
    const options = this.options();
    const columns = this.columns();
    const lastRow = options.length - columns;
    return options.map((option, index) => ({
      option,
      firstRow: index < columns,
      firstColumn: index % columns === 0,
      topLeft: index === 0,
      topRight:
        index === columns - 1 ||
        (index === options.length - 1 && options.length <= columns),
      bottomLeft: index >= lastRow && index % columns === 0,
      bottomRight:
        index >= lastRow &&
        (index % columns === columns - 1 || index === options.length - 1),
    }));
  });

  constructor() {
    super(inject(NgControl));
    afterRenderEffect(onCleanup => {
      const group = this.buttonGroupRef()?.nativeElement;
      if (!group) return;
      this.containerWidth.set(group.getBoundingClientRect().width);
      const observer = new ResizeObserver(([entry]) => {
        if (entry) this.containerWidth.set(entry.contentRect.width);
      });
      observer.observe(group);
      onCleanup(() => observer.disconnect());
    });
  }

  ngOnInit(): void {
    this.subscription.add(
      this.ngControl.control!.valueChanges.subscribe(value => {
        this.ngControl.control!.markAsTouched();
        this.valueChange.emit(value);
      })
    );
  }

  changeActive(option: IButtonOption<T>): void {
    const control = this.ngControl.control!;
    if (control.disabled) return;
    control.setValue(option.value, { emitEvent: false });
    control.updateValueAndValidity();
  }
}
